import { canAccessWorkspace } from "./accessControl";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "./useAuth";
import { RoleEnum, hasRole } from "./constants/roles";
import RiskRegister from "./RiskRegister";
import TaskCentre from "./task-centre/TaskCentre";
import RiskHeatmapDashboard from "./RiskHeatmapDashboard";
import CompanyOverview from "./components/dashboard/CompanyOverview";
import RiskInsights from "./pages/risk/RiskInsights";
import ExecutiveRiskDashboard from "./pages/risk/ExecutiveRiskDashboard";
import ControlsDashboard from "./pages/controls/ControlsDashboard";
import ActionsDashboard from "./ActionsDashboard";
import IncidentRegister from "./IncidentRegister";
import AssetsRegister from "./pages/catalog/AssetsRegister";
import VendorsRegister from "./pages/catalog/VendorsRegister";
import PoliciesRegister from "./pages/catalog/PoliciesRegister";
import SupportingEvidence from "./pages/documents/SupportingEvidence";
import { getFeatureFlag } from "./api/incidents.api";
import { listNotifications, markNotificationRead } from "./api/notifications.api";
import AuditUniverseList from "./pages/audit/AuditUniverseList";
import AuditList from "./pages/audit/AuditList";
import AuditPlans from "./pages/audit/AuditPlans";
import BoardBacksList from "./pages/boardbacks/BoardBacksList";
import KpiList from "./pages/kpis/KpiList";
import ExecutiveKpiDashboard from "./pages/kpis/ExecutiveKpiDashboard";
import UsersAdmin from "./pages/admin/UsersAdmin";
import ActionsAdmin from "./pages/admin/ActionsAdmin";
import IncidentAdmin from "./pages/admin/IncidentAdmin";
import RiskCategoriesAdmin from "./pages/admin/RiskCategoriesAdmin";
import RiskAppetiteAdmin from "./pages/admin/RiskAppetiteAdmin";
import RiskMatrixAdmin from "./pages/admin/RiskMatrixAdmin";
import ControlCategoriesAdmin from "./pages/admin/ControlCategoriesAdmin";
import NotificationsAdmin from "./pages/admin/NotificationsAdmin";
import GovernanceOverview from "./components/dashboard/GovernanceOverview";
import MyOverview from "./components/dashboard/MyOverview";
import Button from "./ui/Button";
import StatusBadge from "./ui/StatusBadge";
import ContentTextSizeControl from "./ui/ContentTextSizeControl";
import { colors, shadows } from "./ui/tokens";
import useContentFontScale from "./hooks/useContentFontScale";
import { GLYDER_NAV_LOGO_URL } from "./brandAssets";
import {
  canAccessAdminModules,
  canAccessAdminUsersStrict,
  canViewActions,
  canViewAudit,
  canViewControls,
  canViewGovernance,
  canViewIncidents,
  canViewKpis,
  canViewPolicies,
  canViewRisks,
  canViewSupportingEvidence,
} from "./accessControl";
import {
  loadRecentViews,
  RECENT_VIEWS_UPDATED_EVENT,
} from "./features/recentViews/recentViews";

const CHECKLIST_STORAGE_KEY = "onboarding.checklist.v1";
const SIDEBAR_SECTION_STORAGE_KEY = "nav.expandedSection.v1";
const SIDEBAR_COLLAPSE_STORAGE_KEY = "nav.sidebarCollapsed.v1";
const ADMIN_PORTAL_MODULE = "admin-modules";
const ADMIN_NOTIFICATIONS_MODULE = "admin-notifications";

const ADMIN_MODULES = [
  {
    key: "admin-risk-categories",
    label: "Risk Categories",
    route: "/admin/risk-categories",
    component: RiskCategoriesAdmin,
    group: "risk-control",
  },
  {
    key: "admin-risk-appetite",
    label: "Risk Appetite",
    route: "/admin/risk-appetite",
    component: RiskAppetiteAdmin,
    group: "risk-control",
  },
  {
    key: "admin-risk-matrix",
    label: "Risk Matrix",
    route: "/admin/risk-matrix",
    component: RiskMatrixAdmin,
    group: "risk-control",
  },
  {
    key: "admin-control-settings",
    label: "Control Settings",
    route: "/admin/control-settings",
    component: ControlCategoriesAdmin,
    group: "risk-control",
  },
  {
    key: "admin-actions",
    label: "Actions Settings",
    route: "/admin/actions",
    component: ActionsAdmin,
    group: "risk-control",
  },
  {
    key: "admin-incidents",
    label: "Incident Settings",
    route: "/admin/incidents",
    component: IncidentAdmin,
    group: "work-response",
  },
];
const DEFAULT_ADMIN_MODULE = ADMIN_MODULES[0]?.key || "admin-risk-categories";
const ADMIN_MODULE_KEYS = ADMIN_MODULES.map((mod) => mod.key);

const compactSectionItems = (items) => {
  const compacted = [];
  let pendingGroup = null;

  items.filter(Boolean).forEach((item) => {
    if (item.type === "group") {
      pendingGroup = item;
      return;
    }
    if (pendingGroup) {
      compacted.push(pendingGroup);
      pendingGroup = null;
    }
    compacted.push(item);
  });

  return compacted;
};

const loadChecklistState = () => {
  try {
    const stored = localStorage.getItem(CHECKLIST_STORAGE_KEY);
    if (!stored) {
      return { dismissed: false, completed: {} };
    }
    const parsed = JSON.parse(stored);
    return {
      dismissed: Boolean(parsed?.dismissed),
      completed: parsed?.completed && typeof parsed.completed === "object" ? parsed.completed : {},
    };
  } catch (_err) {
    return { dismissed: false, completed: {} };
  }
};

export default function DashboardLayout({ initialModule = "executive-summary", children }) {
  const { user, logout, accessReady = true } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { scale: contentFontScale } = useContentFontScale();

  const [expandedSection, setExpandedSection] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_SECTION_STORAGE_KEY) || null;
    } catch (_err) {
      return null;
    }
  });
  const [userCollapsedSection, setUserCollapsedSection] = useState(null);

  const [activeModule, setActiveModule] = useState(initialModule);
  const [incidentEnabled, setIncidentEnabled] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSE_STORAGE_KEY) === "true";
    } catch (_err) {
      return false;
    }
  });
  const [openTopMenu, setOpenTopMenu] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const notifRef = useRef(null);
  const [recentViews, setRecentViews] = useState(() => loadRecentViews());
  const [recentOpen, setRecentOpen] = useState(false);
  const recentRef = useRef(null);
  const [checklistState, setChecklistState] = useState(loadChecklistState);
  const navigationSourceRef = useRef(null);

  useEffect(() => {
    getFeatureFlag("incident_management_enabled")
      .then((res) => {
        const payload = res.data || res;
        const enabled = payload?.enabled !== false;
        setIncidentEnabled(enabled);
      })
      .catch((err) => {
        console.error("Feature flag fetch failed", err);
        setIncidentEnabled(null);
      });
  }, []);

  const toggleSection = (section) => {
    setExpandedSection((prev) => {
      const next = prev === section ? null : section;
      setUserCollapsedSection(prev === section ? section : null);
      return next;
    });
  };

  useEffect(() => {
    try {
      if (expandedSection) {
        localStorage.setItem(SIDEBAR_SECTION_STORAGE_KEY, expandedSection);
      } else {
        localStorage.removeItem(SIDEBAR_SECTION_STORAGE_KEY);
      }
    } catch (_err) {
      // ignore storage errors
    }
  }, [expandedSection]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSE_STORAGE_KEY, String(sidebarCollapsed));
    } catch (_err) {
      // ignore storage errors
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!incidentEnabled && activeModule === "incidents") {
      setActiveModule(null);
    }
  }, [incidentEnabled, activeModule]);

  useEffect(() => {
    if (accessReady && activeModule && !canAccessWorkspace(user, activeModule)) setActiveModule(null);
  }, [accessReady, user, activeModule]);

  const isExecUser = useMemo(
    () => hasRole(user?.role, [RoleEnum.ADMIN, RoleEnum.EXEC, RoleEnum.BOARD]),
    [user]
  );
  const canViewRisksModule = useMemo(() => accessReady && canViewRisks(user), [accessReady, user]);
  const canViewControlsModule = useMemo(() => accessReady && canViewControls(user), [accessReady, user]);
  const canViewActionsModule = useMemo(() => accessReady && canViewActions(user), [accessReady, user]);
  const canViewIncidentsModule = useMemo(() => accessReady && canViewIncidents(user), [accessReady, user]);
  const canViewAuditModule = useMemo(() => accessReady && canViewAudit(user), [accessReady, user]);
  const canViewPoliciesModule = useMemo(() => accessReady && canViewPolicies(user), [accessReady, user]);
  const canViewGovernanceModule = useMemo(() => accessReady && canViewGovernance(user), [accessReady, user]);
  const canViewKpisModule = useMemo(() => accessReady && canViewKpis(user), [accessReady, user]);
  const canAccessRiskExecutive = useMemo(
    () => canViewRisksModule,
    [canViewRisksModule]
  );
  const canManageAdminUsers = useMemo(
    () => accessReady && canAccessAdminUsersStrict(user),
    [accessReady, user]
  );
  const canManageAdminModules = useMemo(
    () => accessReady && canAccessAdminModules(user),
    [accessReady, user]
  );
  const canUseSupportingEvidenceModule = useMemo(
    () => accessReady && canViewSupportingEvidence(user),
    [accessReady, user]
  );
  const orgConfig = useMemo(
    () => ({
      modules: { incidentManagement: true },
    }),
    []
  );

  const showIncidentLabel = useMemo(
    () =>
      incidentEnabled === true &&
      canViewIncidentsModule &&
      (orgConfig?.modules?.incidentManagement ?? false),
    [incidentEnabled, canViewIncidentsModule, orgConfig]
  );

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.read_at).length,
    [notifications]
  );

  const loadNotifications = useCallback(() => {
    if (!user) return;
    listNotifications({ limit: 10 })
      .then((res) => setNotifications(res.data || []))
      .catch(() => setNotifications([]));
  }, [user]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useEffect(() => {
    if (!notifOpen) return;
    const handleClick = (event) => {
      if (!notifRef.current || notifRef.current.contains(event.target)) {
        return;
      }
      setNotifOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [notifOpen]);

  useEffect(() => {
    if (!recentOpen) return;
    const handleClick = (event) => {
      if (!recentRef.current || recentRef.current.contains(event.target)) {
        return;
      }
      setRecentOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [recentOpen]);

  useEffect(() => {
    const handleRecentViewsUpdated = () => {
      setRecentViews(loadRecentViews());
    };
    window.addEventListener(RECENT_VIEWS_UPDATED_EVENT, handleRecentViewsUpdated);
    return () => window.removeEventListener(RECENT_VIEWS_UPDATED_EVENT, handleRecentViewsUpdated);
  }, []);

  const canAccessExecutiveKpis = useMemo(
    () => canViewKpisModule && isExecUser,
    [canViewKpisModule, isExecUser]
  );

  const topNavGroups = useMemo(() => {
    const groups = [
      {
        label: "Aerial View",
        items: [
          { label: "Executive Summary", module: "executive-summary" },
          canAccessRiskExecutive
            ? {
                label: "Executive Risk Dashboard",
                module: "executive-risk-dashboard",
              }
            : null,
          canViewRisksModule ? { label: "Risk Rollup & Tree", module: "risk-insights" } : null,
          canViewRisksModule ? { label: "Risks", module: "risks" } : null,
          canViewRisksModule ? { label: "Risk Heatmap", module: "heatmap" } : null,
          { label: "Tasks", module: "tasks" },
          canViewControlsModule ? { label: "Controls", module: "controls" } : null,
          canViewActionsModule ? { label: "Actions", module: "actions" } : null,
          canViewKpisModule ? { label: "KPIs", module: "kpis" } : null,
          canAccessExecutiveKpis
            ? {
                label: "Executive KPIs",
                module: "executive-kpis",
              }
            : null,
          canViewGovernanceModule ? { label: "Board Packs", module: "boardbacks" } : null,
        ].filter(Boolean),
      },
      {
        label: "Oversight & Control",
        items: [
          canViewAuditModule ? { label: "Audit", module: "audit" } : null,
          showIncidentLabel ? { label: "Incidents", module: "incidents" } : null,
        ].filter(Boolean),
      },
      {
        label: "Catalogs",
        items: [
          canManageAdminModules ? { label: "Assets", module: "assets" } : null,
          canManageAdminModules ? { label: "Vendors", module: "vendors" } : null,
          canViewPoliciesModule ? { label: "Policies", module: "policies" } : null,
        ].filter(Boolean),
      },
      {
        label: "Administration",
        items: [
          canManageAdminUsers
            ? {
                label: "Admin User Portal",
                module: "admin-users",
              }
            : null,
          canManageAdminModules
            ? {
                label: "Notifications",
                module: ADMIN_NOTIFICATIONS_MODULE,
              }
            : null,
          canManageAdminModules
            ? {
                label: "Admin Module Portal",
                module: ADMIN_PORTAL_MODULE,
              }
            : null,
        ].filter(Boolean),
      },
      {
        label: "Journey",
        items: [
          { label: "Company Overview", module: "company-overview" },
          { label: "My Overview", module: "my-overview" },
          { label: "Roadmap", disabled: true },
          { label: "Maturity", disabled: true },
        ],
      },
    ];

    return groups.filter((group) => Array.isArray(group.items) && group.items.length > 0);
  }, [
    showIncidentLabel,
    canViewRisksModule,
    canViewControlsModule,
    canViewActionsModule,
    canViewAuditModule,
    canViewPoliciesModule,
    canViewGovernanceModule,
    canViewKpisModule,
    canAccessRiskExecutive,
    canAccessExecutiveKpis,
    canManageAdminUsers,
    canManageAdminModules,
  ]);

  const sidebarSections = useMemo(
    () => [
      {
        key: "overview",
        title: "Overview",
        items: [
          { label: "Executive Summary", module: "executive-summary", badge: "Executive" },
          { label: "Company Overview", module: "company-overview", badge: "Organization" },
          { label: "My Overview", module: "my-overview", badge: "Personal" },
        ],
      },
      {
        key: "manage",
        title: "Manage",
        items: compactSectionItems([
          { type: "group", label: "Risk & Control" },
          canViewRisksModule ? { label: "Risks", module: "risks", aliases: ["risk-insights", "heatmap"] } : null,
          canViewControlsModule ? { label: "Controls", module: "controls" } : null,
          canViewActionsModule ? { label: "Actions", module: "actions" } : null,
          { type: "group", label: "Work & Response" },
          { label: "Tasks", module: "tasks" },
          showIncidentLabel ? { label: "Incidents", module: "incidents" } : null,
          { type: "group", label: "Audit" },
          canViewAuditModule ? { label: "Audit", module: "audit" } : null,
          { type: "group", label: "Catalogs" },
          canManageAdminModules ? { label: "Assets", module: "assets" } : null,
          canManageAdminModules ? { label: "Vendors", module: "vendors" } : null,
        ]),
      },
      {
        key: "documents",
        title: "Documents",
        items: compactSectionItems([
          ...(canUseSupportingEvidenceModule ? [{ label: "Supporting Evidence", module: "supporting-evidence" }] : []),
          canViewGovernanceModule ? { label: "Board Packs", module: "boardbacks" } : null,
          canViewPoliciesModule ? { label: "Policies", module: "policies" } : null,
        ]),
      },
      {
        key: "reports",
        title: "Reports & Insights",
        items: compactSectionItems([
          canAccessRiskExecutive
            ? {
                label: "Executive Risk Dashboard",
                module: "executive-risk-dashboard",
              }
            : null,
          canAccessExecutiveKpis
            ? {
                label: "Executive KPIs",
                module: "executive-kpis",
              }
            : null,
          canViewKpisModule ? { label: "KPIs", module: "kpis" } : null,
        ]),
      },
      {
        key: "admin",
        title: "Admin",
        items: [
          canManageAdminUsers
            ? {
                label: "Admin User Portal",
                module: "admin-users",
              }
            : null,
          canManageAdminModules
            ? {
                label: "Notifications",
                module: ADMIN_NOTIFICATIONS_MODULE,
              }
            : null,
          canManageAdminModules
            ? {
                label: "Admin Module Portal",
                module: ADMIN_PORTAL_MODULE,
                aliases: ADMIN_MODULE_KEYS,
              }
            : null,
        ].filter(Boolean),
      },
    ].map(section => ({ ...section, items: section.items.filter(item => !item.module || (accessReady && canAccessWorkspace(user, item.module))) })),
    [
      accessReady,
      user,
      showIncidentLabel,
      canViewRisksModule,
      canViewControlsModule,
      canViewActionsModule,
      canViewAuditModule,
      canViewPoliciesModule,
      canViewGovernanceModule,
      canViewKpisModule,
      canAccessRiskExecutive,
      canAccessExecutiveKpis,
      canManageAdminUsers,
      canManageAdminModules,
      canUseSupportingEvidenceModule,
    ]
  );

  const sidebarSectionByModule = useMemo(() => {
    const mapping = {};
    sidebarSections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.module) {
          mapping[item.module] = section.key;
        }
        if (Array.isArray(item.aliases)) {
          item.aliases.forEach((alias) => {
            mapping[alias] = section.key;
          });
        }
      });
    });
    return mapping;
  }, [sidebarSections]);

  const checklistSteps = useMemo(
    () => [
      {
        key: "add-risks",
        label: "Add risks",
        description: "Create the first risks to establish posture.",
        module: "risks",
        action: "openAddRisk",
      },
      {
        key: "review-rollup",
        label: "Review risk rollup",
        description: "See category counts and the risk tree structure.",
        module: "risk-insights",
      },
      {
        key: "link-controls",
        label: "Link controls",
        description: "Connect controls to the risks they mitigate.",
        module: "controls",
      },
      {
        key: "assign-actions",
        label: "Assign actions",
        description: "Track remediation work and owners.",
        module: "actions",
      },
    ],
    []
  );

  const allChecklistDone = useMemo(
    () => checklistSteps.every((step) => checklistState.completed?.[step.key]),
    [checklistSteps, checklistState]
  );

  const showChecklist = !checklistState.dismissed && !allChecklistDone;

  useEffect(() => {
    try {
      localStorage.setItem(CHECKLIST_STORAGE_KEY, JSON.stringify(checklistState));
    } catch (_err) {
      // ignore storage errors
    }
  }, [checklistState]);

  const markChecklistComplete = useCallback((key) => {
    setChecklistState((prev) => {
      if (prev.completed?.[key]) return prev;
      return {
        ...prev,
        completed: { ...prev.completed, [key]: true },
      };
    });
  }, []);

  const dismissChecklist = useCallback(() => {
    setChecklistState((prev) => ({ ...prev, dismissed: true }));
  }, []);

  const handleModuleSelect = (module) => {
    if (!module) return;
    navigationSourceRef.current = "user";
    if (module === ADMIN_PORTAL_MODULE) {
      const defaultModule = DEFAULT_ADMIN_MODULE;
      const defaultRoute =
        ADMIN_MODULES.find((mod) => mod.key === defaultModule)?.route ||
        "/admin/risk-categories";
      setActiveModule(defaultModule);
      setOpenTopMenu(null);
      navigate(defaultRoute);
      return;
    }
    setActiveModule(module);
    setOpenTopMenu(null);
    const catalogRoutes = {
      assets: "/catalog/assets",
      vendors: "/catalog/vendors",
      policies: "/catalog/policies",
    };
    const documentRoutes = {
      "supporting-evidence": "/documents/supporting-evidence",
    };
    if (module === "executive-kpis") {
      navigate("/executive/kpis");
      return;
    }
    if (module === "executive-risk-dashboard") {
      navigate("/executive/risk-dashboard");
      return;
    }
    if (module === "admin-users") {
      navigate("/admin/users");
      return;
    }
    if (module === "admin-control-settings") {
      navigate("/admin/control-settings");
      return;
    }
    if (module === "admin-risk-categories") {
      navigate("/admin/risk-categories");
      return;
    }
    if (module === "admin-risk-appetite") {
      navigate("/admin/risk-appetite");
      return;
    }
    if (module === "admin-risk-matrix") {
      navigate("/admin/risk-matrix");
      return;
    }
    if (module === "admin-actions") {
      navigate("/admin/actions");
      return;
    }
    if (module === "admin-incidents") {
      navigate("/admin/incidents");
      return;
    }
    if (module === ADMIN_NOTIFICATIONS_MODULE) {
      navigate("/admin/notifications");
      return;
    }
    if (module === "audit") {
      navigate("/audit");
      return;
    }
    if (catalogRoutes[module]) {
      navigate(catalogRoutes[module]);
      return;
    }
    if (documentRoutes[module]) {
      navigate(documentRoutes[module]);
      return;
    }
    if (location.pathname.startsWith("/risks/")) {
      // Leaving a full-page risk detail route requires returning to the shell route.
      if (["risks", "risk-insights", "heatmap"].includes(module)) {
        navigate("/risks");
      } else {
        navigate("/");
      }
      return;
    }
    if (location.pathname.startsWith("/controls/")) {
      navigate("/");
      return;
    }
    if (location.pathname.startsWith("/audit")) {
      navigate("/");
      return;
    }
    if (location.pathname.startsWith("/executive/kpis")) {
      navigate("/");
    }
    if (location.pathname.startsWith("/executive/risk-dashboard")) {
      navigate("/");
    }
    if (location.pathname.startsWith("/admin/users")) {
      navigate("/");
    }
    if (
      location.pathname.startsWith("/admin/control-settings") ||
      location.pathname.startsWith("/admin/control-categories")
    ) {
      navigate("/");
    }
    if (location.pathname.startsWith("/admin/risk-categories")) {
      navigate("/");
    }
    if (location.pathname.startsWith("/admin/risk-appetite")) {
      navigate("/");
    }
    if (location.pathname.startsWith("/admin/risk-matrix")) {
      navigate("/");
    }
    if (location.pathname.startsWith("/admin/actions")) {
      navigate("/");
    }
    if (location.pathname.startsWith("/admin/incidents")) {
      navigate("/");
    }
    if (location.pathname.startsWith("/admin/notifications")) {
      navigate("/");
    }
    if (location.pathname.startsWith("/catalog/")) {
      navigate("/");
    }
  };

  useEffect(() => {
    if (!showChecklist) return;
    const moduleMap = {
      risks: "add-risks",
      "risk-insights": "review-rollup",
      controls: "link-controls",
      actions: "assign-actions",
    };
    const stepKey = moduleMap[activeModule];
    if (stepKey) {
      markChecklistComplete(stepKey);
    }
  }, [activeModule, markChecklistComplete, showChecklist]);

  useEffect(() => {
    const handler = (event) => {
      const detail = event?.detail;
      if (!detail) return;
      const nextModule = typeof detail === "string" ? detail : detail.module;
      if (nextModule) {
        navigationSourceRef.current = "user";
        setActiveModule((current) => (current === nextModule ? current : nextModule));
      }
      const actionName = detail?.action;
      if (typeof actionName === "string" && actionName.length > 0) {
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent(actionName));
        }, 0);
      }
    };
    window.addEventListener("navigateModule", handler);
    return () => window.removeEventListener("navigateModule", handler);
  }, []);

  useEffect(() => {
    let nextModule = null;
    if (location.pathname.startsWith("/controls/")) {
      nextModule = "controls";
    } else 
    if (location.pathname.startsWith("/executive/kpis")) {
      nextModule = "executive-kpis";
    } else if (location.pathname.startsWith("/executive/risk-dashboard")) {
      nextModule = "executive-risk-dashboard";
    } else if (location.pathname.startsWith("/admin/users")) {
      nextModule = "admin-users";
    } else if (
      location.pathname.startsWith("/admin/control-settings") ||
      location.pathname.startsWith("/admin/control-categories")
    ) {
      nextModule = "admin-control-settings";
    } else if (location.pathname.startsWith("/admin/risk-categories")) {
      nextModule = "admin-risk-categories";
    } else if (location.pathname.startsWith("/admin/risk-appetite")) {
      nextModule = "admin-risk-appetite";
    } else if (location.pathname.startsWith("/admin/risk-matrix")) {
      nextModule = "admin-risk-matrix";
    } else if (location.pathname.startsWith("/admin/actions")) {
      nextModule = "admin-actions";
    } else if (location.pathname.startsWith("/admin/incidents")) {
      nextModule = "admin-incidents";
    } else if (location.pathname.startsWith("/admin/notifications")) {
      nextModule = ADMIN_NOTIFICATIONS_MODULE;
    } else if (location.pathname.startsWith("/catalog/assets")) {
      nextModule = "assets";
    } else if (location.pathname.startsWith("/catalog/vendors")) {
      nextModule = "vendors";
    } else if (location.pathname.startsWith("/catalog/policies")) {
      nextModule = "policies";
    } else if (location.pathname.startsWith("/documents/supporting-evidence")) {
      nextModule = "supporting-evidence";
    }
    if (!nextModule) {
      if (navigationSourceRef.current === "user" || navigationSourceRef.current === "route") {
        navigationSourceRef.current = null;
      }
      return;
    }
    if (navigationSourceRef.current === "user") {
      if (nextModule === activeModule) {
        navigationSourceRef.current = null;
        return;
      }
      if (location.pathname !== "/") {
        navigate("/", { replace: true });
      }
      navigationSourceRef.current = null;
      return;
    }
    if (nextModule !== activeModule) {
      navigationSourceRef.current = "route";
      setActiveModule(nextModule);
      return;
    }
    if (navigationSourceRef.current === "route") {
      navigationSourceRef.current = null;
    }
  }, [activeModule, location.pathname, navigate]);

  useEffect(() => {
    if (navigationSourceRef.current === "user") return;
    if (location.pathname.startsWith("/executive/kpis") && activeModule !== "executive-kpis") {
      navigate("/");
    }
    if (location.pathname.startsWith("/executive/risk-dashboard") && activeModule !== "executive-risk-dashboard") {
      navigate("/");
    }
    if (location.pathname.startsWith("/admin/users") && activeModule !== "admin-users") {
      navigate("/");
    }
    if (
      (location.pathname.startsWith("/admin/control-settings") ||
        location.pathname.startsWith("/admin/control-categories")) &&
      !["admin-control-settings", ADMIN_PORTAL_MODULE].includes(activeModule)
    ) {
      navigate("/");
    }
    if (
      location.pathname.startsWith("/admin/risk-categories") &&
      !["admin-risk-categories", ADMIN_PORTAL_MODULE].includes(activeModule)
    ) {
      navigate("/");
    }
    if (
      location.pathname.startsWith("/admin/risk-appetite") &&
      !["admin-risk-appetite", ADMIN_PORTAL_MODULE].includes(activeModule)
    ) {
      navigate("/");
    }
    if (
      location.pathname.startsWith("/admin/risk-matrix") &&
      !["admin-risk-matrix", ADMIN_PORTAL_MODULE].includes(activeModule)
    ) {
      navigate("/");
    }
    if (
      location.pathname.startsWith("/admin/actions") &&
      !["admin-actions", ADMIN_PORTAL_MODULE].includes(activeModule)
    ) {
      navigate("/");
    }
    if (
      location.pathname.startsWith("/admin/incidents") &&
      !["admin-incidents", ADMIN_PORTAL_MODULE].includes(activeModule)
    ) {
      navigate("/");
    }
    if (
      location.pathname.startsWith("/admin/notifications") &&
      activeModule !== ADMIN_NOTIFICATIONS_MODULE
    ) {
      navigate("/");
    }
    if (location.pathname.startsWith("/catalog/assets") && activeModule !== "assets") {
      navigate("/");
    }
    if (location.pathname.startsWith("/catalog/vendors") && activeModule !== "vendors") {
      navigate("/");
    }
    if (location.pathname.startsWith("/catalog/policies") && activeModule !== "policies") {
      navigate("/");
    }
    if (location.pathname.startsWith("/documents/supporting-evidence") && activeModule !== "supporting-evidence") {
      navigate("/");
    }
  }, [activeModule, location.pathname, navigate]);

  useEffect(() => {
    if (!activeModule) return;
    const nextSection = sidebarSectionByModule[activeModule];
    if (userCollapsedSection && userCollapsedSection !== nextSection) {
      setUserCollapsedSection(null);
    }
    if (expandedSection !== null) return;
    if (!nextSection) return;
    if (userCollapsedSection === nextSection) return;
    setExpandedSection(nextSection);
  }, [activeModule, expandedSection, sidebarSectionByModule, userCollapsedSection]);


  return (
    <div className={["company-overview", "heatmap"].includes(activeModule) ? "glyder-chart-workspace" : undefined} style={{ height: "100vh", display: "flex", flexDirection: "column" }}>
      <div
        className="glyder-workspace-topbar"
        style={{
          background: colors.topbarBackground,
          height: "60px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 20px",
          color: colors.onAccent,
          borderBottom: `1px solid ${colors.topbarSurfaceHover}`,
          flexShrink: 0,
        }}
      >
        <div style={{ display: "flex", gap: "12px" }}>
          {topNavGroups.map((group) => (
            <TopNavDropdown
              key={group.label}
              label={group.label}
              items={group.items}
              activeModule={activeModule}
              openKey={openTopMenu}
              setOpenKey={setOpenTopMenu}
              onSelect={handleModuleSelect}
            />
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <ContentTextSizeControl />
          <div ref={recentRef} style={{ position: "relative" }}>
            <Button
              onClick={() => {
                setRecentOpen((prev) => {
                  const next = !prev;
                  if (next) {
                    setRecentViews(loadRecentViews());
                    setNotifOpen(false);
                  }
                  return next;
                });
              }}
              data-testid="recently-viewed-button"
              variant="ghost"
              style={{
                border: "none",
                background: colors.topbarSurface,
                color: colors.onAccent,
                borderRadius: "999px",
                minHeight: "40px",
                padding: "0 14px",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
              }}
              title="Recently viewed"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 5c5.8 0 10.2 5.3 11.4 7-.9 1.3-5.1 7-11.4 7S1.8 13.3.6 12C1.5 10.7 6.2 5 12 5zm0 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0-2.2a1.8 1.8 0 1 1 0-3.6 1.8 1.8 0 0 1 0 3.6z"
                  fill="currentColor"
                />
              </svg>
              <span>Recently Viewed</span>
            </Button>
            {recentOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "46px",
                  right: 0,
                  width: "360px",
                  background: colors.topbarSurface,
                  color: colors.onAccent,
                  border: `1px solid ${colors.topbarSurfaceHover}`,
                  borderRadius: "12px",
                  padding: "10px",
                  zIndex: 30,
                  boxShadow: shadows.md,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: "8px" }}>Recently viewed</div>
                {recentViews.length === 0 && (
                  <div style={{ fontSize: "12px", color: colors.chromeTextMuted }}>No recently viewed items yet.</div>
                )}
                {recentViews.map((item) => (
                  <Button
                    key={`${item.itemType}:${item.itemId}`}
                    onClick={() => {
                      setRecentOpen(false);
                      navigate(item.route);
                    }}
                    variant="ghost"
                    style={{
                      width: "100%",
                      textAlign: "left",
                      padding: "8px",
                      borderRadius: "8px",
                      border: "1px solid transparent",
                      background: "transparent",
                      color: colors.onAccent,
                      cursor: "pointer",
                      marginBottom: "6px",
                    }}
                  >
                    <div style={{ fontSize: "11px", color: colors.chromeTextMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      {item.moduleLabel}
                    </div>
                    <div style={{ fontSize: "13px", fontWeight: 600, marginTop: "2px" }}>{item.title}</div>
                    {item.secondaryText && (
                      <div style={{ fontSize: "12px", color: colors.chromeText, marginTop: "2px" }}>
                        {item.secondaryText}
                      </div>
                    )}
                  </Button>
                ))}
              </div>
            )}
          </div>
          <div ref={notifRef} style={{ position: "relative" }}>
            <Button
              onClick={() => {
                setNotifOpen((prev) => {
                  const next = !prev;
                  if (next) {
                    loadNotifications();
                    setRecentOpen(false);
                  }
                  return next;
                });
              }}
              data-testid="notifications-bell"
              variant="ghost"
              style={{
                border: "none",
                background: colors.topbarSurface,
                color: colors.onAccent,
                borderRadius: "999px",
                width: "40px",
                height: "40px",
                cursor: "pointer",
                position: "relative",
              }}
              title="Notifications"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  d="M12 3c-3 0-5 2-5 5v3c0 .7-.3 1.4-.8 1.9L5 14h14l-1.2-1.1c-.5-.5-.8-1.2-.8-1.9V8c0-3-2-5-5-5zm0 18a2.5 2.5 0 0 0 2.4-2H9.6a2.5 2.5 0 0 0 2.4 2z"
                  fill="currentColor"
                />
              </svg>
              {unreadCount > 0 && (
                <span
                  data-testid="notifications-count"
                  style={{
                    position: "absolute",
                    top: "-4px",
                    right: "-4px",
                    background: colors.danger,
                    color: colors.onAccent,
                    borderRadius: "999px",
                    padding: "2px 6px",
                    fontSize: "11px",
                    fontWeight: 700,
                  }}
                >
                  {unreadCount}
                </span>
              )}
            </Button>
            {notifOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "46px",
                  right: 0,
                  width: "320px",
                  background: colors.topbarSurface,
                  color: colors.onAccent,
                  border: `1px solid ${colors.topbarSurfaceHover}`,
                  borderRadius: "12px",
                  padding: "10px",
                  zIndex: 30,
                  boxShadow: shadows.md,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: "8px" }}>Notifications</div>
                {notifications.length === 0 && (
                  <div style={{ fontSize: "12px", color: colors.chromeTextMuted }}>No notifications yet.</div>
                )}
                {notifications.slice(0, 10).map((item) => {
                  const unread = !item.read_at;
                  return (
                    <Button
                      key={item.id}
                      onClick={() => {
                        if (unread) {
                          markNotificationRead(item.id)
                            .then(() => loadNotifications())
                            .catch(() => loadNotifications());
                        }
                        if (item.entity_type === "kpi" && item.entity_id) {
                          navigate(`/kpis/${item.entity_id}`);
                        }
                        setNotifOpen(false);
                      }}
                      variant="ghost"
                      style={{
                        width: "100%",
                        textAlign: "left",
                        padding: "8px",
                        borderRadius: "8px",
                        border: "1px solid transparent",
                        background: unread ? colors.sidebarActive : "transparent",
                        color: colors.onAccent,
                        cursor: "pointer",
                        marginBottom: "6px",
                      }}
                    >
                      <div style={{ fontSize: "13px", fontWeight: 600 }}>{item.title}</div>
                      {item.body && (
                        <div style={{ fontSize: "12px", color: colors.chromeText, marginTop: "2px" }}>
                          {item.body}
                        </div>
                      )}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>
          <span style={{ color: colors.chromeText }}>{user?.username}</span>
        </div>
      </div>

        <div className="glyder-workspace-body" style={{ display: "flex", flex: 1, height: "calc(100vh - 60px)" }}>
          <div
            className="glyder-workspace-sidebar"
            style={{
              width: sidebarCollapsed ? "96px" : "320px",
              background: `linear-gradient(180deg, ${colors.sidebarBackground} 0%, ${colors.sidebarDarker} 100%)`,
              color: colors.onAccent,
              padding: sidebarCollapsed ? "8px 12px" : "10px 22px",
              overflowY: "auto",
              transition: "width 0.2s ease, padding 0.2s ease",
            }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
              marginBottom: sidebarCollapsed ? "4px" : "6px",
            }}
          >
            <div
              onClick={() => setActiveModule("executive-summary")}
              style={{
                cursor: "pointer",
                marginBottom: 0,
                display: "flex",
                alignItems: "center",
                minHeight: sidebarCollapsed ? "86px" : "118px",
                padding: sidebarCollapsed ? "2px 0" : "4px 0",
                justifyContent: sidebarCollapsed ? "center" : "flex-start",
                flex: 1,
              }}
              aria-label="Glyder home"
            >
              <img
                src={GLYDER_NAV_LOGO_URL}
                alt="Glyder"
                style={{
                  width: sidebarCollapsed ? "84px" : "100%",
                  maxWidth: sidebarCollapsed ? "84px" : "276px",
                  height: "auto",
                  display: "block",
                  filter: "none",
                  mixBlendMode: "normal",
                  opacity: 1,
                  objectFit: "contain",
                }}
              />
            </div>
            <Button
              variant="ghost"
              onClick={() => setSidebarCollapsed((prev) => !prev)}
              aria-label={sidebarCollapsed ? "Expand navigation" : "Collapse navigation"}
              style={{
                background: colors.sidebarActive,
                border: `1px solid ${colors.sidebarActiveBorder}`,
                color: colors.onAccent,
                width: "28px",
                height: "28px",
                padding: 0,
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "none",
              }}
            >
              {sidebarCollapsed ? ">" : "<"}
            </Button>
          </div>

          {!sidebarCollapsed && (
            <>
              {sidebarSections.map((section) => (
                <SidebarGroup
                  key={section.key}
                  title={section.title}
                  expanded={expandedSection === section.key}
                  onToggle={() => toggleSection(section.key)}
                >
                  {section.items.map((item) => {
                    const itemIsActive =
                      item.module === activeModule ||
                      (Array.isArray(item.aliases) && item.aliases.includes(activeModule));
                    return (
                      <SidebarNavItem
                        key={item.label}
                        item={item}
                        isActive={itemIsActive}
                        onSelect={handleModuleSelect}
                      />
                    );
                  })}
                </SidebarGroup>
              ))}

              <Button
                onClick={logout}
                variant="secondary"
                style={{
                  marginTop: 40,
                  background: colors.surface,
                  color: colors.textPrimary,
                  padding: "10px 8px",
                  borderColor: colors.border,
                }}
              >
                Log out
              </Button>
            </>
          )}
        </div>

        <div
          className="glyder-workspace-content"
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "20px",
            background: colors.pageBackground,
            fontSize: "calc(1rem * var(--content-font-scale))",
            "--content-font-scale": String(contentFontScale),
          }}
        >
          {showChecklist && (
            <OnboardingChecklist
              steps={checklistSteps}
              completed={checklistState.completed}
              onDismiss={dismissChecklist}
              onNavigate={(step) => {
                setActiveModule(step.module);
                if (step.action === "openAddRisk") {
                  setTimeout(() => {
                    window.dispatchEvent(new CustomEvent("openAddRisk"));
                  }, 0);
                }
              }}
            />
          )}
          {children || (accessReady && canAccessWorkspace(user, activeModule) ? (
            <>
              {(activeModule === "executive-summary" || !activeModule) && (
                <ExecutiveSummary onNavigate={setActiveModule} />
              )}
              {activeModule === "company-overview" && (
                <CompanyOverview onNavigate={setActiveModule} />
              )}
              {activeModule === "my-overview" && (
                <MyOverview />
              )}
              {["risks", "risk-insights", "heatmap"].includes(activeModule) && (
                <RisksWorkspace activeModule={activeModule} onNavigate={setActiveModule} />
              )}
              {activeModule === "tasks" && <TaskCentre />}
              {activeModule === "controls" && <ControlsDashboard />}
              {activeModule === "actions" && <ActionsDashboard />}
              {activeModule === "assets" && <AssetsRegister />}
              {activeModule === "vendors" && <VendorsRegister />}
              {activeModule === "policies" && <PoliciesRegister />}
              {activeModule === "supporting-evidence" &&
                (canUseSupportingEvidenceModule ? (
                  <SupportingEvidence />
                ) : (
                  <OverviewPanel
                    title="Supporting Evidence access required"
                    intro="You need at least View access to a Glyder module that contributes supporting evidence to open this workspace."
                  />
                ))}
              {activeModule === "kpis" && <KpiList />}
              {activeModule === "executive-kpis" && <ExecutiveKpiDashboard />}
              {activeModule === "executive-risk-dashboard" && <ExecutiveRiskDashboard />}
              {activeModule === "incidents" && <IncidentRegister />}
              {activeModule === "audit-universe" && <AuditUniverseList />}
              {activeModule === "audit-plans" && <AuditPlans />}
              {activeModule === "audits" && <AuditList />}
              {activeModule === "boardbacks" && <BoardBacksList />}
              {activeModule === "admin-users" && <UsersAdmin />}
              {activeModule === ADMIN_NOTIFICATIONS_MODULE && <NotificationsAdmin />}
              {(ADMIN_MODULE_KEYS.includes(activeModule) || activeModule === ADMIN_PORTAL_MODULE) && (
                <AdminModulePortal
                  activeModule={activeModule}
                  defaultModuleKey={DEFAULT_ADMIN_MODULE}
                  modules={ADMIN_MODULES}
                  onSelect={handleModuleSelect}
                />
              )}
            </>
          ) : <p>Module access required.</p>)}

        </div>
      </div>
    </div>
  );
}

function RisksWorkspace({ activeModule, onNavigate }) {
  const tabs = [
    { label: "Register", module: "risks" },
    { label: "Rollup & Tree", module: "risk-insights" },
    { label: "Heatmap", module: "heatmap" },
    { label: "Trends", comingSoon: true },
    { label: "Severity", comingSoon: true },
  ];

  let content = <RiskRegister />;
  if (activeModule === "risk-insights") {
    content = <RiskInsights />;
  }
  if (activeModule === "heatmap") {
    content = <RiskHeatmapDashboard />;
  }

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {tabs.map((tab) => {
          const isActive = tab.module && activeModule === tab.module;
          const isDisabled = tab.comingSoon || !tab.module;

          return (
            <Button
              key={tab.label}
              type="button"
              onClick={() => {
                if (isDisabled) return;
                onNavigate && onNavigate(tab.module);
              }}
              variant="secondary"
              style={{
                ...riskTabStyle,
                ...(isActive ? riskTabActiveStyle : null),
                ...(isDisabled ? riskTabDisabledStyle : null),
              }}
              disabled={isDisabled}
              aria-disabled={isDisabled}
            >
              <span>{tab.label}</span>
              {tab.comingSoon && <span style={riskTabBadgeStyle}>Coming soon</span>}
            </Button>
          );
        })}
      </div>
      {content}
    </div>
  );
}

function ExecutiveSummary({ onNavigate }) {
  const workspaceSignals = [
    {
      title: "Risk posture",
      description: "Use the risk rollup and heatmap views to review concentration, severity, and review cadence.",
      cta: "Open Risk Rollup & Tree",
      module: "risk-insights",
    },
    {
      title: "Operational follow-up",
      description: "Review assigned actions and task queues to confirm owners, due dates, and execution status.",
      cta: "Review Actions",
      module: "actions",
    },
    {
      title: "Governance coverage",
      description: "Check policy and control coverage to confirm published guidance and linked safeguards remain current.",
      cta: "Open Policies",
      module: "policies",
    },
  ];

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <section
        style={{
          background: colors.surface,
          borderRadius: "12px",
          border: `1px solid ${colors.border}`,
          padding: "16px",
        }}
      >
        <h2 style={{ margin: 0, marginBottom: "6px" }}>Executive Summary</h2>
        <p style={{ margin: 0, color: colors.textMuted, lineHeight: 1.5 }}>
          Glyder gives leadership teams a clear starting point for risk posture, governance oversight, and follow-up
          actions without leaving the core workspace.
        </p>
        <p style={{ marginTop: "8px", marginBottom: 0, color: colors.textMuted, fontSize: "13px" }}>
          Suggested flow: review posture first, inspect structure next, then move directly into the actions and
          governance work that needs attention.
        </p>
        <div style={{ marginTop: "12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Button
            onClick={() => onNavigate && onNavigate("risk-insights")}
            variant="primary"
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: `1px solid ${colors.brandPrimary}`,
              background: colors.brandPrimary,
              color: colors.onAccent,
              cursor: "pointer",
            }}
          >
            Start with Risk Rollup & Tree
          </Button>
          <Button
            onClick={() => onNavigate && onNavigate("heatmap")}
            variant="secondary"
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: `1px solid ${colors.border}`,
              background: colors.surfaceSubtle,
              cursor: "pointer",
            }}
          >
            See Risk Heatmap
          </Button>
          <Button
            onClick={() => onNavigate && onNavigate("actions")}
            variant="secondary"
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: `1px solid ${colors.border}`,
              background: colors.surfaceSubtle,
              cursor: "pointer",
            }}
          >
            Review Actions
          </Button>
        </div>
        <div style={{ marginTop: "12px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          {["Risks", "Controls", "Audit", "Incidents", "Vendors", "Policies", "Tasks"].map((label) => (
            <span
              key={label}
              style={{
                padding: "6px 10px",
                borderRadius: "999px",
                background: colors.surfaceMuted,
                fontSize: "12px",
                color: colors.textPrimary,
              }}
            >
              {label}
            </span>
          ))}
        </div>
        <div style={{ marginTop: "14px", display: "flex", gap: "8px", flexWrap: "wrap" }}>
          <Button
            onClick={() => onNavigate && onNavigate("company-overview")}
            variant="secondary"
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: `1px solid ${colors.border}`,
              background: colors.surfaceSubtle,
              cursor: "pointer",
            }}
          >
            Company overview
          </Button>
          <Button
            onClick={() => onNavigate && onNavigate("my-overview")}
            variant="secondary"
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: `1px solid ${colors.border}`,
              background: colors.surfaceSubtle,
              cursor: "pointer",
            }}
          >
            My overview
          </Button>
        </div>
      </section>

      <GovernanceOverview />

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "12px",
        }}
      >
        {workspaceSignals.map((card) => (
          <div
            key={card.title}
            style={{
              background: colors.surface,
              borderRadius: "12px",
              border: `1px solid ${colors.border}`,
              padding: "14px",
              minHeight: "140px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxShadow: shadows.xs,
            }}
          >
            <div>
              <div style={{ fontWeight: 700 }}>{card.title}</div>
              <div style={{ fontSize: "12px", color: colors.textMuted, marginTop: "4px", lineHeight: 1.55 }}>
                {card.description}
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onNavigate && onNavigate(card.module)}
              style={{ marginTop: "14px", justifySelf: "start", alignSelf: "flex-start" }}
            >
              {card.cta}
            </Button>
          </div>
        ))}
      </section>
    </div>
  );
}

function AdminModulePortal({ activeModule, defaultModuleKey, modules, onSelect }) {
  // Admin Module Portal mirrors left-nav group -> module structure. Add future groups here.
  const portalGroups = useMemo(
    () => [
      {
        key: "risk-control",
        label: "Risk & Control",
        modules: modules.filter((mod) => mod.group === "risk-control").map((mod) => ({ ...mod, placeholder: false })),
      },
      {
        key: "work-response",
        label: "Work & Response",
        modules: [
          ...modules.filter((mod) => mod.group === "work-response").map((mod) => ({ ...mod, placeholder: false })),
          {
            key: "tasks",
            label: "Tasks",
            placeholder: true,
            placeholderText:
              "Task administration is not enabled in this workspace yet. When available, this area will centralize workflow defaults, ownership conventions, and supporting task configuration.",
          },
        ],
      },
    ].filter((section) => Array.isArray(section.modules) && section.modules.length > 0),
    [modules]
  );

  const activeGroupKey =
    portalGroups.find((group) => group.modules.some((mod) => mod.key === activeModule))?.key || null;
  const [selectedGroupKey, setSelectedGroupKey] = useState(
    activeGroupKey || portalGroups[0]?.key || "risk-control"
  );
  const [selectedPlaceholderKey, setSelectedPlaceholderKey] = useState(null);
  const previousActiveGroupKeyRef = useRef(activeGroupKey);

  useEffect(() => {
    if (
      activeGroupKey &&
      previousActiveGroupKeyRef.current &&
      previousActiveGroupKeyRef.current !== activeGroupKey
    ) {
      setSelectedGroupKey(activeGroupKey);
      setSelectedPlaceholderKey(null);
    }
    previousActiveGroupKeyRef.current = activeGroupKey;
  }, [activeGroupKey]);

  const resolvedKey = modules.some((mod) => mod.key === activeModule) ? activeModule : defaultModuleKey;
  const resolved = modules.find((mod) => mod.key === resolvedKey) || modules[0];
  const ActiveComponent = resolved?.component || null;

  const selectedGroup = portalGroups.find((group) => group.key === selectedGroupKey) || portalGroups[0];
  const selectedPlaceholder =
    selectedGroup?.modules.find((mod) => mod.placeholder && mod.key === selectedPlaceholderKey) || null;
  const selectedGroupHasActiveModule = Boolean(
    selectedGroup?.modules.some((mod) => !mod.placeholder && mod.key === resolvedKey)
  );

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <section
        style={{
          background: colors.surface,
          borderRadius: "12px",
          border: `1px solid ${colors.border}`,
          padding: "16px",
          display: "grid",
          gap: "8px",
        }}
      >
        <div style={{ fontSize: 12, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Admin Module Portal
        </div>
        <div style={{ fontWeight: 700, fontSize: 18 }}>Administration modules</div>
        <div style={{ fontSize: 13, color: colors.textMuted }}>
          This workspace hosts module navigation for admin configuration.
        </div>
      </section>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {portalGroups.map((group) => {
          const isActive = selectedGroupKey === group.key;
          return (
            <Button
              key={group.key}
              type="button"
              variant={isActive ? "primary" : "secondary"}
              onClick={() => {
                setSelectedGroupKey(group.key);
                setSelectedPlaceholderKey(null);
              }}
              style={{ padding: "8px 12px", borderRadius: "999px" }}
            >
              {group.label}
            </Button>
          );
        })}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", paddingLeft: "4px" }}>
        {selectedGroup?.modules.map((mod) => {
          const isPlaceholder = Boolean(mod.placeholder);
          const isActive =
            (!isPlaceholder && activeModule === mod.key) ||
            (isPlaceholder && selectedPlaceholderKey === mod.key);
          return (
            <Button
              key={mod.key}
              type="button"
              variant={isActive ? "primary" : "secondary"}
              onClick={() => {
                if (isPlaceholder) {
                  setSelectedPlaceholderKey(mod.key);
                  return;
                }
                setSelectedPlaceholderKey(null);
                onSelect && onSelect(mod.key);
              }}
              style={{ padding: "6px 12px", borderRadius: "10px" }}
            >
              {mod.label}
            </Button>
          );
        })}
      </div>

      {selectedGroupHasActiveModule && ActiveComponent ? <ActiveComponent /> : null}
      {selectedPlaceholder ? (
        <section
          style={{
            background: colors.surface,
            borderRadius: "12px",
            border: `1px solid ${colors.border}`,
            padding: "16px",
            display: "grid",
            gap: "8px",
            boxShadow: shadows.xs,
          }}
        >
          <div style={{ fontSize: 12, color: colors.textMuted, textTransform: "uppercase", letterSpacing: "0.08em" }}>
            Configuration Area
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: colors.textPrimary }}>
            {selectedPlaceholder.label} settings are not enabled yet
          </div>
          <div style={{ color: colors.textMuted, fontSize: "13px", lineHeight: 1.6 }}>
            {selectedPlaceholder.placeholderText}
          </div>
        </section>
      ) : null}
      {!selectedGroupHasActiveModule && !selectedPlaceholder ? (
        <section
          style={{
            background: colors.surface,
            borderRadius: "12px",
            border: `1px dashed ${colors.borderStrong}`,
            padding: "16px",
            color: colors.textMuted,
            fontSize: "13px",
          }}
        >
          Select a module to begin configuration.
        </section>
      ) : null}
    </div>
  );
}

function OverviewPanel({ title, intro }) {
  const overviewCards = [
    {
      title: "Priority views",
      description: "Open the most relevant registers, dashboards, and governance screens for this overview.",
    },
    {
      title: "Operational signals",
      description: "Track the work queues, review cycles, and ownership patterns that need leadership attention.",
    },
    {
      title: "Guided follow-up",
      description: "Use linked actions and policies to move from observation into accountable next steps.",
    },
  ];

  return (
    <div style={{ display: "grid", gap: "16px" }}>
      <section
        style={{
          background: colors.surface,
          borderRadius: "12px",
          border: `1px solid ${colors.border}`,
          padding: "16px",
        }}
      >
        <h2 style={{ margin: 0, marginBottom: "6px" }}>{title}</h2>
        <p style={{ margin: 0, color: colors.textMuted, lineHeight: 1.5 }}>{intro}</p>
      </section>

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "12px",
        }}
      >
        {overviewCards.map((card) => (
          <div
            key={card.title}
            style={{
              background: colors.surface,
              borderRadius: "12px",
              border: `1px solid ${colors.border}`,
              padding: "14px",
              minHeight: "140px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              boxShadow: shadows.xs,
            }}
          >
            <div>
              <div style={{ fontWeight: 700 }}>{card.title}</div>
              <div style={{ fontSize: "12px", color: colors.textMuted, marginTop: "4px", lineHeight: 1.55 }}>
                {card.description}
              </div>
            </div>
            <div
              style={{
                marginTop: "12px",
                borderRadius: "10px",
                border: `1px solid ${colors.border}`,
                background: colors.surfaceSubtle,
                minHeight: "72px",
                display: "grid",
                alignContent: "center",
                gap: "4px",
                padding: "12px",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: 600, color: colors.textPrimary }}>Workspace ready</div>
              <div style={{ color: colors.textSubtle, fontSize: "12px", lineHeight: 1.5 }}>
                Use the left navigation to open the relevant workspace for this view.
              </div>
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

function OnboardingChecklist({ steps, completed, onDismiss, onNavigate }) {
  return (
    <section className="glyder-onboarding"
      style={{
        background: colors.surface,
        borderRadius: 12,
        border: `1px solid ${colors.border}`,
        padding: "16px",
        marginBottom: "16px",
        boxShadow: shadows.sm,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <div>
          <div style={{ fontSize: 12, letterSpacing: "0.08em", color: colors.textMuted, textTransform: "uppercase" }}>
            Start Here
          </div>
          <h3 style={{ margin: "4px 0" }}>Onboarding checklist</h3>
          <p style={{ margin: 0, color: colors.textMuted, fontSize: 13 }}>
            Follow the quick steps below to see your risk posture, understand structure, and take action.
          </p>
        </div>
        <Button variant="ghost" onClick={onDismiss} style={{ color: colors.textMuted }}>
          Dismiss
        </Button>
      </div>

      <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
        {steps.map((step) => {
          const done = !!completed?.[step.key];
          return (
            <div
              key={step.key}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                padding: "10px 12px",
                borderRadius: 10,
                border: `1px solid ${colors.border}`,
                background: done ? colors.successSubtle : colors.surfaceSubtle,
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 999,
                    border: `2px solid ${done ? colors.success : colors.borderStrong}`,
                    background: done ? colors.success : "transparent",
                    color: done ? colors.onAccent : colors.textSubtle,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    fontWeight: 700,
                  }}
                >
                  {done ? "x" : ""}
                </div>
                <div>
                  <div style={{ fontWeight: 600, color: colors.textPrimary }}>{step.label}</div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>{step.description}</div>
                </div>
              </div>
              <Button
                variant={done ? "secondary" : "primary"}
                onClick={() => onNavigate(step)}
                style={{ minWidth: 110 }}
              >
                {done ? "Revisit" : "Start"}
              </Button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function SidebarGroup({ title, expanded, onToggle, children }) {
  const contentRef = useRef(null);
  const [height, setHeight] = useState(expanded ? "auto" : "0px");
  const heightRef = useRef(height);

  useEffect(() => {
    heightRef.current = height;
  }, [height]);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    if (expanded) {
      const scrollHeight = el.scrollHeight;
      setHeight(scrollHeight + "px");
      const timeout = setTimeout(() => setHeight("auto"), 220);
      return () => clearTimeout(timeout);
    } else {
      if (heightRef.current === "auto") {
        const scrollHeight = el.scrollHeight;
        setHeight(scrollHeight + "px");
        requestAnimationFrame(() => setHeight("0px"));
      } else {
        setHeight("0px");
      }
    }
  }, [expanded]);

  const items = React.Children.toArray(children);

  return (
    <div style={{ marginTop: "6px" }}>
      <Button
        onClick={onToggle}
        variant="ghost"
        style={{
          background: "transparent",
          border: "none",
          color: colors.onAccent,
          fontSize: "18px",
          textAlign: "left",
          width: "100%",
          padding: "6px 0",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          boxShadow: "none",
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: "20px",
            transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
            transformOrigin: "center",
            transition: "transform 0.18s ease",
          }}
        >
          >
        </span>
        {title}
      </Button>

      <div
        ref={contentRef}
        style={{
          overflow: "hidden",
          height,
          transition: "height 0.22s ease",
          marginLeft: "15px",
        }}
      >
        <div style={{ paddingTop: "2px" }}>
          {items.map((child, index) => (
            <div
              key={index}
              style={{
                opacity: expanded ? 1 : 0,
                transform: expanded ? "translateY(0)" : "translateY(-4px)",
                transition: "opacity 0.18s ease, transform 0.18s ease",
                transitionDelay: expanded ? `${index * 35}ms` : "0ms",
              }}
            >
              {child}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SidebarNavItem({ item, isActive, onSelect }) {
  if (item.type === "group") {
    return <div style={sidebarGroupLabelStyle}>{item.label}</div>;
  }

  const isLocked = Boolean(item.disabledReason);
  const isComingSoon = !isLocked && (item.comingSoon || !item.module);
  const isDisabled = isLocked || isComingSoon || item.disabled;
  const disabledReason = item.disabledReason || (isComingSoon ? "Coming soon." : "");
  const badgeLabel = isLocked ? "Locked" : isComingSoon ? "Coming soon" : item.badge;
  const badgeVariant =
    !isLocked && !isComingSoon && item.badgeTone === "warning" ? "kpi" : "status";
  const badgeValue = isLocked ? "blocked" : isComingSoon ? "pending" : item.badgeTone === "warning" ? "yellow" : badgeLabel;
  const ariaParts = [item.label];
  if (badgeLabel && badgeLabel !== "Locked" && badgeLabel !== "Coming soon") {
    ariaParts.push(badgeLabel);
  }
  if (isDisabled && disabledReason) {
    ariaParts.push(disabledReason);
  }
  const ariaLabel = ariaParts.join(". ");

  return (
    <Button
      type="button"
      style={{
        ...sidebarItemStyle,
        background: isActive ? colors.sidebarActive : "transparent",
        boxShadow: isActive ? `inset 3px 0 0 ${colors.sidebarActiveBorder}` : "none",
        color: isActive ? colors.accentSubtle : colors.onAccent,
        cursor: isDisabled ? "not-allowed" : "pointer",
        opacity: isDisabled ? 0.6 : 1,
      }}
      onClick={() => {
        if (isDisabled) return;
        onSelect(item.module);
      }}
      aria-disabled={isDisabled}
      aria-label={ariaLabel}
      aria-current={isActive ? "page" : undefined}
      title={isDisabled && disabledReason ? disabledReason : undefined}
    >
      <span>{item.label}</span>
      {badgeLabel && (
        <StatusBadge variant={badgeVariant} value={badgeValue} label={badgeLabel} />
      )}
    </Button>
  );
}

function TopNavDropdown({ label, items, activeModule, openKey, setOpenKey, onSelect }) {
  const containerRef = useRef(null);
  const isOpen = openKey === label;

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (event) => {
      if (!containerRef.current || containerRef.current.contains(event.target)) return;
      setOpenKey(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, setOpenKey]);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <Button
        variant="ghost"
        style={{
          background: colors.topbarSurface,
          color: colors.onAccent,
          border: `1px solid ${colors.topbarSurfaceHover}`,
          padding: "10px 22px",
          borderRadius: "999px",
          transition: "0.2s ease",
          boxShadow: "none",
        }}
        onClick={() => setOpenKey(isOpen ? null : label)}
        onMouseOver={(e) => (e.currentTarget.style.background = colors.topbarSurfaceHover)}
        onMouseOut={(e) => (e.currentTarget.style.background = colors.topbarSurface)}
      >
        {label}
      </Button>
      {isOpen && (
        <div
          style={{
            position: "absolute",
            top: "46px",
            left: 0,
            background: colors.topbarSurface,
            border: `1px solid ${colors.topbarSurfaceHover}`,
            borderRadius: "12px",
            padding: "8px",
            minWidth: "210px",
            zIndex: 20,
            boxShadow: shadows.md,
          }}
        >
          {items.map((item) => {
            const disabled = item.disabled || !item.module;
            const isActive = item.module && activeModule === item.module;
            const disabledReason = item.disabledReason || (disabled ? "Coming soon." : "");
            const badgeLabel = disabled ? (item.disabledReason ? "Locked" : "Coming soon") : item.badge;
            const badgeVariant =
              !disabled && item.badgeTone === "warning" ? "kpi" : "status";
            const badgeValue = disabled
              ? item.disabledReason
                ? "blocked"
                : "pending"
              : item.badgeTone === "warning"
                ? "yellow"
                : badgeLabel;
            const ariaParts = [item.label];
            if (!disabled && item.badge) {
              ariaParts.push(item.badge);
            }
            if (disabled && disabledReason) {
              ariaParts.push(disabledReason);
            }
            const ariaLabel = ariaParts.join(". ");

            return (
              <div
                key={item.label}
                style={{
                  ...topMenuItemStyle,
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.5 : 1,
                  background: isActive ? colors.sidebarActive : "transparent",
                  color: isActive ? colors.accentSubtle : colors.onAccent,
                }}
                onClick={() => {
                  if (disabled) return;
                  onSelect(item.module);
                }}
                role="button"
                tabIndex={disabled ? 0 : undefined}
                aria-disabled={disabled}
                aria-label={ariaLabel}
                aria-current={isActive ? "page" : undefined}
                title={disabled ? disabledReason : undefined}
              >
                <span>{item.label}</span>
                {badgeLabel && (
                  <StatusBadge variant={badgeVariant} value={badgeValue} label={badgeLabel} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const topMenuItemStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  padding: "8px 10px",
  borderRadius: "8px",
  fontSize: "13px",
};

const sidebarItemStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  width: "100%",
  padding: "8px 10px",
  borderRadius: "10px",
  fontSize: "14px",
  border: "1px solid transparent",
  background: "transparent",
  textAlign: "left",
  fontFamily: "inherit",
};

const sidebarGroupLabelStyle = {
  margin: "12px 8px 4px",
  fontSize: "11px",
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  color: colors.chromeTextMuted,
};

const riskTabStyle = {
  display: "flex",
  alignItems: "center",
  gap: "6px",
  border: `1px solid ${colors.border}`,
  background: colors.surfaceSubtle,
  color: colors.textPrimary,
  borderRadius: "999px",
  padding: "6px 12px",
  fontSize: "12px",
  cursor: "pointer",
};

const riskTabActiveStyle = {
  background: colors.brandPrimary,
  borderColor: colors.brandPrimary,
  color: colors.onAccent,
};

const riskTabDisabledStyle = {
  cursor: "not-allowed",
  opacity: 0.6,
};

const riskTabBadgeStyle = {
  fontSize: "10px",
  padding: "2px 6px",
  borderRadius: "999px",
  background: colors.surfaceMuted,
  color: colors.textMuted,
};

